import { RefreshCwIcon } from 'lucide-react'

import { useReprojectOrgCompany, useReprojectOrgCompanyEngagement } from '@/api/company.hook'

import { useIsSupportUser } from '@/lib/app/support-user'

import { Button } from '@/components/lemmala-design-system/button/button'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { useToast } from '@/components/lemmala-design-system/toast/use-toast'

type ReprojectOrgCompanyButtonProps = {
  orgCompanyId: string
  // When set, reprojects only this engagement instead of the whole company.
  engagementId?: string
}

export const ReprojectOrgCompanyButton = ({
  orgCompanyId,
  engagementId,
}: ReprojectOrgCompanyButtonProps) => {
  const isSupportUser = useIsSupportUser()
  const { toast } = useToast()
  const { mutateAsync: reprojectOrgCompany, isPending: isCompanyPending } = useReprojectOrgCompany()
  const { mutateAsync: reprojectEngagement, isPending: isEngagementPending } =
    useReprojectOrgCompanyEngagement()

  if (!isSupportUser) return null

  const isPending = engagementId ? isEngagementPending : isCompanyPending
  const label = engagementId ? 'Reproject engagement' : 'Reproject org company'

  const handleReproject = async () => {
    try {
      if (engagementId) {
        await reprojectEngagement({ orgCompanyEngagementId: engagementId })
      } else {
        await reprojectOrgCompany({ orgCompanyId })
      }
      toast({
        title: 'Reprojection started successfully',
        description: `The ${engagementId ? 'engagement' : 'org company'} reprojection has been initiated.`,
        variant: 'success',
      })
    } catch {
      toast({
        title: 'Failed to start reprojection',
        description: 'An error occurred while starting the reprojection.',
        variant: 'destructive',
      })
    }
  }

  return (
    <Button size={'sm'} variant={'outline'} disabled={isPending} onClick={handleReproject}>
      <TextWithIcon
        icon={<RefreshCwIcon size={14} className={isPending ? 'animate-spin' : ''} />}
        text={isPending ? 'Reprojecting...' : label}
      />
    </Button>
  )
}
